import { useState } from 'react';
import { navigationTree, type NavNode } from '@life-os/app-core';
import { Link, useRouter } from './router.js';
import { RouteIcon } from './icons.js';

function Chevron({ open }: { open: boolean }) {
  return (
    <svg className="icon" viewBox="0 0 24 24" width={13} height={13} aria-hidden="true" data-open={open}>
      <path d="m9 6 6 6-6 6" stroke="currentColor" strokeWidth="2.2" fill="none" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function NavLeaf({ node, depth }: { node: NavNode; depth: number }) {
  const { path } = useRouter();
  const [open, setOpen] = useState(true);
  const hasChildren = Boolean(node.children?.length);
  const active = node.route === path;

  return (
    <div>
      <div className="nav-row" style={{ paddingLeft: depth * 12 }}>
        {hasChildren ? (
          <button className="nav-disclosure" data-open={open} aria-label={open ? 'Collapse' : 'Expand'} onClick={() => setOpen((o) => !o)}>
            <Chevron open={open} />
          </button>
        ) : (
          <span className="nav-disclosure nav-disclosure--spacer" />
        )}
        {node.route ? (
          <Link to={node.route} className="nav-link">
            <span className="nav-link__icon">
              <RouteIcon route={node.route} size={17} />
            </span>
            <span className="nav-link__label" data-active={active}>
              {node.label}
            </span>
          </Link>
        ) : (
          <span className="nav-link nav-link--branch">
            <span className="nav-link__label">{node.label}</span>
          </span>
        )}
      </div>
      {hasChildren && open ? (
        <div>
          {node.children!.map((c) => (
            <NavLeaf key={c.label + (c.route ?? '')} node={c} depth={depth + 1} />
          ))}
        </div>
      ) : null}
    </div>
  );
}

export function Sidebar({ onOpenPalette }: { onOpenPalette: () => void }) {
  const [openSections, setOpenSections] = useState<Record<string, boolean>>(() =>
    Object.fromEntries(navigationTree.map((s) => [s.id, s.defaultOpen])),
  );

  return (
    <aside className="app__side">
      <div className="app__brand">
        <span className="app__brand-mark">
          <svg viewBox="0 0 24 24" width={17} height={17} aria-hidden="true" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M12 3a9 9 0 1 0 9 9" />
            <path d="M12 7v5l3 2" />
          </svg>
        </span>
        <div>
          <div>Life OS</div>
          <div className="app__brand-sub">Personal OS</div>
        </div>
      </div>

      <button className="app__search" onClick={onOpenPalette}>
        <svg className="icon" viewBox="0 0 24 24" width={16} height={16} aria-hidden="true">
          <circle cx="11" cy="11" r="7" stroke="currentColor" strokeWidth="2" fill="none" />
          <path d="m21 21-4.3-4.3" stroke="currentColor" strokeWidth="2" fill="none" strokeLinecap="round" />
        </svg>
        Search everything
        <kbd>⌘K</kbd>
      </button>

      <nav className="app__nav" aria-label="Primary">
        {navigationTree.map((section) => {
          const open = openSections[section.id] ?? section.defaultOpen;
          return (
            <div key={section.id} className="nav-section">
              <button
                className="nav-section__head"
                aria-expanded={open}
                onClick={() => setOpenSections((s) => ({ ...s, [section.id]: !open }))}
              >
                <span>{section.label}</span>
                <span className="nav-section__chevron" data-open={open}>
                  <Chevron open={open} />
                </span>
              </button>
              {open ? (
                <div className="nav-section__body">
                  {section.items.map((node) => (
                    <NavLeaf key={node.label + (node.route ?? '')} node={node} depth={0} />
                  ))}
                </div>
              ) : null}
            </div>
          );
        })}
      </nav>
    </aside>
  );
}
